/**
 * Meal Review Swap hand-off (sessionStorage, this tab only).
 *
 * Meal Review's plan is an UNSAVED draft that lives only in PlanReview's
 * state. Its Swap opens the regular Plan Swap browser (/meals?swap=true),
 * which unmounts PlanReview, so:
 *   1. PlanReview saves its working draft + the Swap's draft-day request here
 *      and navigates to the browser with `draftSwap=1`;
 *   2. the browser sends that request (instead of reading the saved day) and
 *      records the facts of the cards it loaded;
 *   3. a pick (card or candidate detail, via performSwap's draft branch)
 *      records ONLY the chosen meal here -- never a meal-plan write;
 *   4. the finished Swap pops back to Review, which restores the draft,
 *      replaces exactly that row and re-checks the draft. Confirm & Save is
 *      still the only write.
 */

var KEY = 'fitxess.planReviewDraftSwap';

function read() {
  try {
    var raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function write(record) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(record));
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Start a draft Swap. `record` = { reviewKey, state, request } where
 * `request` = { date, rows, outgoing_row_key, replace_plan_ids,
 * outgoing_meal_id } is the draft day the browser sends. Returns false when
 * the draft could not be stored (the Swap must not open then).
 */
export function startDraftSwap(record) {
  return write(Object.assign({}, record, { candidates: {}, pick: null }));
}

/** The pending draft Swap, or null. */
export function readDraftSwap() {
  return read();
}

/** The draft-day request for the browser, or null when there is none. */
export function draftSwapRequest() {
  var record = read();
  return record && record.request ? record.request : null;
}

/** Remember the facts of the cards the browser loaded (merged, by meal_id). */
export function rememberDraftSwapCandidates(meals) {
  var record = read();
  if (!record) return;
  var candidates = Object.assign({}, record.candidates);
  (meals || []).forEach(function (m) {
    if (!m || !m.meal_id) return;
    candidates[m.meal_id] = {
      meal_name: m.name || m.meal_name || '',
      calories: m.calories || 0, protein: m.protein || 0, carbs: m.carbs || 0, fat: m.fat || 0,
      omitted_ingredient_ids: m.omitted_ingredient_ids || [],
      draft_saveable: m.draft_saveable !== false,
      draft_block_reason: m.draft_block_reason || null,
    };
  });
  write(Object.assign({}, record, { candidates: candidates }));
}

function draftError(code) {
  var err = new Error('Draft swap not applied: ' + code);
  err.draftSwapCode = code;
  return err;
}

/**
 * Record the replacement for the draft row `rowKey`. Rejects (and records
 * nothing) when there is no matching draft Swap, the meal was not among the
 * loaded cards, or Confirm & Save would reject it.
 */
export function recordDraftSwapPick(rowKey, meal) {
  var record = read();
  if (!record || !record.request || record.request.outgoing_row_key !== rowKey) {
    return Promise.reject(draftError('draft_missing'));
  }
  var facts = meal && record.candidates ? record.candidates[meal.meal_id] : null;
  if (!facts) return Promise.reject(draftError('draft_missing'));
  if (!facts.draft_saveable) return Promise.reject(draftError(facts.draft_block_reason || 'draft_missing'));
  var pick = {
    row_key: rowKey,
    meal_id: meal.meal_id,
    meal_name: facts.meal_name || meal.name || '',
    calories: facts.calories, protein: facts.protein, carbs: facts.carbs, fat: facts.fat,
    omitted_ingredient_ids: facts.omitted_ingredient_ids,
  };
  if (!write(Object.assign({}, record, { pick: pick }))) return Promise.reject(draftError('draft_missing'));
  return Promise.resolve();
}

/**
 * End the draft Swap: Review calls this when it takes the result back. A
 * record left behind (e.g. Review was closed instead) is inert -- it is only
 * ever restored into the same Review history entry -- and the next Swap
 * replaces it.
 */
export function clearDraftSwap() {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) { /* ignore */ }
}
